import React from 'react';
import styled from 'styled-components';
import {Detail, TextArea} from '@forecast-it/design-system';
import {PromptTarget} from '../state/useNovaPromptState';
import {NOVA_MONOSPACE_FONT} from '../shared/novaStudioFonts';

// Prompt bodies are trusted, verbatim text: they are shown in a fixed-width font so variable tokens
// and whitespace read exactly as authored. The design system's TextArea has no monospace prop, so the
// font is applied to the rendered textarea here (it sets its own font and would not inherit one).
const EditorShell = styled.div`
	& textarea {
		font-family: ${NOVA_MONOSPACE_FONT};
	}
`;

// Spacing only — the caption naming the editor a variable insertion will land in is a
// design-system `Detail`, so its size and colour come from the type scale rather than from here.
const ActiveHintRow = styled.div`
	margin-top: 4px;
`;

// Spacing only — the issue rows bring their own type and colour from the design system.
const IssuesRow = styled.div`
	margin-top: 8px;
`;

interface MonospacePromptEditorProps {
	label: string;
	dataCy: string;
	target: PromptTarget;
	value: string;
	height: number;
	onChange: (value: string) => void;
	onActivate: (target: PromptTarget, caretOffset?: number) => void;
	caption?: string;
	// Guidance shown under the label — what this input is for.
	helperText?: string;
	// Marks the input as one an agent can be complete without.
	isOptional?: boolean;
	// Advisory feedback about this input's content, rendered directly beneath it so the editor and
	// what is wrong with it stay one unit across all three inputs.
	issues?: React.ReactNode;
}

// A single prompt-body editor (System prompt or User-prompt template): a monospace design-system
// TextArea that reports which editor is focused, and where the caret sits, into the shared
// prompt-state so a later variable insertion targets the right place. React's events bubble, so the
// wrapper's handlers fire for the inner textarea; its `selectionStart` is the caret. An optional
// caption names this editor as the active insertion target.
export function MonospacePromptEditor({
	label,
	dataCy,
	target,
	value,
	height,
	onChange,
	onActivate,
	caption,
	helperText,
	isOptional,
	issues,
}: MonospacePromptEditorProps): JSX.Element {
	// The caret is re-reported on every event that can move it, not just on focus: typing, pasting
	// and deleting (`input`), caret-only keys such as the arrows and Home/End (`keyup`), and a plain
	// click that places the caret without creating a selection (`click`) — browsers fire no `select`
	// event for those, so reporting on focus/select alone would leave the recorded caret stale at
	// wherever it was when the editor was first focused, and an insertion would land there.
	// Non-textarea children of the wrapper (the label) have no `selectionStart` and are ignored, so
	// clicking the label doesn't report a caret of its own.
	const reportActive = (event: React.SyntheticEvent<HTMLDivElement>): void => {
		const node = event.target as HTMLTextAreaElement;
		if (typeof node.selectionStart !== 'number') {
			return;
		}
		onActivate(target, node.selectionStart);
	};
	return (
		<div>
			<EditorShell
				data-cy={`${dataCy}-shell`}
				onFocus={reportActive}
				onSelect={reportActive}
				onInput={reportActive}
				onKeyUp={reportActive}
				onClick={reportActive}
			>
				<TextArea
					label={label}
					data-cy={dataCy}
					value={value}
					onChange={(changed: string) => onChange(changed)}
					onBlur={() => undefined}
					isOptional={isOptional ?? false}
					helperText={helperText}
					height={height}
					width={TextArea.WIDTH.FLEX}
				/>
			</EditorShell>
			{caption ? (
				<ActiveHintRow>
					<Detail color="medium" data-cy={`${dataCy}-active-hint`}>
						{caption}
					</Detail>
				</ActiveHintRow>
			) : null}
			{issues ? <IssuesRow data-cy={`${dataCy}-issues-row`}>{issues}</IssuesRow> : null}
		</div>
	);
}
